import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import {
  CharitableGoalDataDTO,
  FORM_ID,
  UPSERT_GOAL_URL,
  validationMap,
} from './CharitableGiving.meta';
import { GoalComponentProps as Props } from '../../components/Goal/Goal.meta';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { CharitableGivingOptions, useGoalsQuery } from '../../api/useGoalsQuery';
import { DAF } from './components/Forms/DAF/DAF';
import { GivingStrategy } from '../../../../CharitableGivingWorkflow/stages/CharitableGiving/api/useCharitableGivingQuery';
import { OutrightGiftsToCharity } from './components/Forms/OutrightGiftsToCharity/OutrightGiftsToCharity';
import { QualifiedCharitableDonations } from './components/Forms/QualifiedCharitableDonations/QualifiedCharitableDonations';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { EmptyForm } from './components/EmptyForm/EmptyForm';
import { selectCurrentClient } from '../../../../CommonSelectors/CommonAreaSelector';

export const ComponentMap = {
  [GivingStrategy.DonationToDAF]: DAF,
  [GivingStrategy.OutrightGiftsToCharity]: OutrightGiftsToCharity,
  [GivingStrategy.QualifiedCharitableDonations]: QualifiedCharitableDonations,
};

export const CharitableGiving = (props: Props<CharitableGoalDataDTO>) => {
  const { clientId, goalData } = props;

  const [givingStrategy, setGivingStrategy] = useState<GivingStrategy | null>(
    goalData.givingStrategy ?? null,
  );
  const { data } = useGoalsQuery({ clientId });

  if (!data) {
    return null;
  }

  const { charitableGivingOptions } = data;

  if (givingStrategy === null) {
    return <EmptyForm options={charitableGivingOptions} onChange={setGivingStrategy} {...props} />;
  }

  return (
    <CharitableGivingContent
      {...props}
      options={charitableGivingOptions}
      givingStrategy={givingStrategy}
    />
  );
};

interface ContentProps extends Props<CharitableGoalDataDTO> {
  options: CharitableGivingOptions;
  givingStrategy: GivingStrategy;
}

const CharitableGivingContent = ({
  onClose,
  givingStrategy,
  assetBucketingIndices,
  ...rest
}: ContentProps) => {
  const { options, goalData, clientId, id } = rest;

  const canMaximizeDonations = givingStrategy === GivingStrategy.QualifiedCharitableDonations;

  const { Dob: principalDob, PartnerDob: partnerDob } = useSelector(selectCurrentClient);

  const { isRecurring = false } = goalData;

  const { data } = useGoalsQuery({ clientId });

  if (!data) {
    return null;
  }

  const {
    assetBucketingOptions: { enableGoalAssetBucketing },
  } = data;

  const formBag = useForm<CharitableGoalDataDTO>({
    defaultValues: {
      ...goalData,
      assetBucketingIndices,
      principalDob,
      partnerDob,
      givingStrategy,
      maximizeDonations: (canMaximizeDonations && goalData.maximizeDonations) || false,
      isRecurring,
    },
    resolver: yupResolver(validationMap[givingStrategy]),
    context: {
      isRecurring,
    },
  });

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: UPSERT_GOAL_URL });

  const { control, handleSubmit, watch } = formBag;

  const givingStrategyValue = watch('givingStrategy');

  const FormComponent = ComponentMap[givingStrategyValue ?? GivingStrategy.DonationToDAF];

  const { givingStrategyOptions } = options;

  const onSubmit = handleSubmit(async formData => {
    try {
      await upsertGoal(formData);

      onClose();
    } catch (error) {
      console.error(error);
    }
  });

  return (
    <UpsertGoalModal formId={FORM_ID} isLoading={isUpsertingGoal} onClose={onClose} {...rest}>
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <form id={FORM_ID} onSubmit={onSubmit} aria-label="Estate Planning goal form">
        <Box display="flex" flexDirection="column" css="gap: 16px">
          <Controller
            control={control}
            name="givingStrategy"
            render={({ field: { value, onChange } }) => (
              <Select
                label="Giving Strategy"
                value={givingStrategyOptions.find(opt => opt.value === value) || null}
                options={givingStrategyOptions}
                onChange={opt => onChange(opt?.value)}
                disabled={!!id}
                isPortal
              />
            )}
          />

          <FormProvider {...formBag}>
            <FormComponent
              options={options}
              clientId={clientId}
              hasAssetBucketing={enableGoalAssetBucketing}
              goalId={id}
            />
          </FormProvider>
        </Box>
      </form>
    </UpsertGoalModal>
  );
};
